<script lang="ts">
  import "@fontsource-variable/source-sans-3";
  import "../app.css";
  import Bugsnag from "@bugsnag/js";
  import { Capacitor } from "@capacitor/core";
  import { QueryClientProvider } from "@tanstack/svelte-query";
  import { onMount } from "svelte";
  import { afterNavigate, goto } from "$app/navigation";
  import { page } from "$app/state";
  import { branding } from "$lib/branding";
  import OfflineBanner from "$lib/components/OfflineBanner.svelte";
  import UpdateToast from "$lib/components/UpdateToast.svelte";
  import { WHITELABEL_KEY } from "$lib/globals";
  import { initNative } from "$lib/native/init";
  import { darkMode } from "$lib/prefs.svelte";
  import { queryClient } from "$lib/query/client";
  import ErrorScreen from "$lib/shell/ErrorScreen.svelte";
  import { showsNotFound } from "$lib/shell/errors";
  import DialogHost from "$lib/ui/DialogHost.svelte";
  import { errorLog } from "$lib/utils/logger";
  import type { LayoutProps } from "./$types";

  let { children }: LayoutProps = $props();

  // NJ has a single theme, without a dark mode.
  const theme = $derived(
    WHITELABEL_KEY === "nj" ? "docket-nj" : darkMode.current ? "docket-dark" : "docket",
  );

  // A page that fails to render shows the error screen until the app goes to another URL, as the
  // React router's error elements did.
  let recover: (() => void) | undefined;
  let failedAt = "";

  function renderFailed(error: unknown, reset: () => void) {
    errorLog(error, "Could not show the page");
    Bugsnag.notify(error instanceof Error ? error : new Error(String(error)));
    recover = reset;
    failedAt = page.url.href;
  }

  // The apps' listeners, once the router can take the links that open the app.
  onMount(() => {
    const stopping = initNative(goto).catch((err: unknown) => {
      errorLog(err, "Could not start the apps' listeners");
      return () => Promise.resolve();
    });
    return () => void stopping.then((stop) => stop());
  });

  afterNavigate(({ to }) => {
    if (recover && to?.url.href !== failedAt) {
      const reset = recover;
      recover = undefined;
      reset();
    }
  });
</script>

<svelte:head>
  <title>{branding.title}</title>
  <link rel="icon" type="image/svg+xml" href="{branding.faviconPrefix}/favicon.svg" />
  <link rel="shortcut icon" href="{branding.faviconPrefix}/favicon.ico" />
  <link
    rel="apple-touch-icon"
    sizes="180x180"
    href="{branding.faviconPrefix}/apple-touch-icon.png"
  />
  <meta name="apple-mobile-web-app-title" content={branding.title} />
  <link rel="manifest" href="{branding.faviconPrefix}/site.webmanifest" />
</svelte:head>

<QueryClientProvider client={queryClient}>
  <div
    data-theme={theme}
    class="h-full max-md:bg-secondary md:bg-linear-to-b from-neutral to-secondary max-md:has-[.override-bg-to-neutral]:bg-neutral has-[.override-bg-use-gradient]:bg-linear-to-b pt-[env(safe-area-inset-top)] jersey:from-info jersey:to-info"
  >
    {#if !Capacitor.isNativePlatform()}
      <UpdateToast />
      <OfflineBanner />
    {/if}
    <svelte:boundary onerror={renderFailed}>
      {@render children()}

      {#snippet failed()}
        <ErrorScreen notFound={showsNotFound(500, page.url.pathname)} />
      {/snippet}
    </svelte:boundary>
    <DialogHost />
  </div>
</QueryClientProvider>
